import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  IUserWorkspaceWithDomain,
  type IWorkspaceSelector,
} from '@principle-theorem/ng-auth';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import {
  InputSearchFilter,
  TypedFormControl,
  toSearchInput$,
} from '@principle-theorem/ng-shared';
import {
  WorkspaceType,
  isPartnerWorkspace,
} from '@principle-theorem/principle-core/interfaces';
import { snapshot, type IUserWorkspace } from '@principle-theorem/shared';
import { first, sortBy } from 'lodash';
import { ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  INgPrincipleConfig,
  NG_PRINCIPLE_CONFIG,
} from '../../ng-principle-config';

@Component({
  selector: 'pr-partner-workspace-selector',
  templateUrl: './partner-workspace-selector.component.html',
  styleUrls: ['./partner-workspace-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, NgMaterialModule],
})
export class PartnerWorkspaceSelectorComponent implements IWorkspaceSelector {
  searchCtrl = new TypedFormControl<string>('');
  workspaceSearch: InputSearchFilter<IUserWorkspace<WorkspaceType.Partner>>;
  workspaces$: ReplaySubject<IUserWorkspace<WorkspaceType.Partner>[]> =
    new ReplaySubject(1);

  @Output()
  workspaceSelected = new EventEmitter<IUserWorkspaceWithDomain>();

  @Input()
  set workspaces(workspaces: IUserWorkspace[]) {
    if (workspaces) {
      this.workspaces$.next(
        sortBy(workspaces.filter(isPartnerWorkspace), 'workspace')
      );
    }
  }

  constructor(
    @Inject(NG_PRINCIPLE_CONFIG) private _config: INgPrincipleConfig
  ) {
    this.workspaceSearch = new InputSearchFilter<
      IUserWorkspace<WorkspaceType.Partner>
    >(this.workspaces$, toSearchInput$(this.searchCtrl), ['workspace']);
  }

  async selectFirstOption(): Promise<void> {
    const firstOption = await snapshot(
      this.workspaceSearch.results$.pipe(map((workspaces) => first(workspaces)))
    );
    if (firstOption) {
      this.selectWorkspace(firstOption);
    }
  }

  selectWorkspace(workspace: IUserWorkspace<WorkspaceType.Partner>): void {
    const domainUrl = window.location.origin;
    const localhostUrl = 'http://localhost';
    const partnerPortalPort = 4203;
    const localPartnersAppUrl = `${localhostUrl}:${partnerPortalPort}`;
    const isLocalhost = domainUrl.includes(localhostUrl);
    const redirectDomain = isLocalhost
      ? localPartnersAppUrl
      : this._config.partnersAppUrl;

    this.workspaceSelected.emit({
      ...workspace,
      redirectDomain,
    });
  }
}
